'use client';
import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties, ComponentType } from 'react';
import type { Dealer, InventoryDealerJoin } from 'app/components/types';
import type { StockMapProps } from './createStockPage';
import type { FlaggedData } from './stockTypes';
import { StockMapLoading } from './stockMapLoading';

// Inventory rows load in the browser via the /api/inventory proxy instead of
// being serialized into the RSC payload. The rows are multi-MB for the bigger
// models, and embedding them in the page HTML meant mobile parsed + hydrated
// the whole set before the map could even mount. createStockPage renders
// <link rel="preload"> for these URLs so the downloads start at HTML parse.
//
// /mapRows is /data with the options blob slimmed to the filter keys AND no
// dealer join — dealer fields repeated per car were ~40% of the payload and
// the dealer list is already fetched separately, so this is where the two
// get joined back into the InventoryDealerJoin shape the map components
// expect. The fallback chain (/mapData → /data, both pre-joined) covers the
// deploy-skew window where the web build is newer than the inventory-api
// worker; re-joining already-joined rows is a harmless overwrite.

const EMPTY_FLAGGED: FlaggedData = {
  vins: [],
  dealers: [],
  vinsMarkedSold: [],
};

async function fetchJson<T>(
  url: string,
  ...fallbackUrls: string[]
): Promise<T> {
  let res = await fetch(url);
  for (const fallback of fallbackUrls) {
    if (res.status !== 404) break;
    res = await fetch(fallback);
  }
  if (!res.ok) throw new Error(`${url} failed: ${res.status}`);
  return (await res.json()) as T;
}

/** Rebuild the InventoryDealerJoin shape from joinless /mapRows rows + the
 *  dealer list. A dealer missing from the lookup leaves its fields absent —
 *  equivalent to the SQL left-join's nulls for every consumer (invalid
 *  lat/lng markers are filtered out either way). Rows that arrive pre-joined
 *  (fallback endpoints) pass through unchanged since row fields win. */
function joinDealers(
  rows: InventoryDealerJoin[],
  dealers: Dealer[],
): InventoryDealerJoin[] {
  const byId: Record<string, Dealer> = {};
  for (const d of dealers) byId[d.dealerID] = d;
  return rows.map((r) => ({ ...byId[r.dealerID], ...r }));
}

async function withRetry<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      if (i < attempts - 1)
        await new Promise((r) => setTimeout(r, 600 * (i + 1)));
    }
  }
  throw lastErr;
}

const errorWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  minHeight: '60vh',
  color: 'var(--fg-3, #64748b)',
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 13,
  letterSpacing: '0.04em',
};

export function StockDataClient({
  model,
  make,
  MapComponent,
  initialSubmodel,
}: {
  model: string;
  make: string;
  MapComponent: ComponentType<StockMapProps>;
  initialSubmodel?: string;
}) {
  const [data, setData] = useState<{
    markers: InventoryDealerJoin[];
    dealers: Dealer[];
    flagged: FlaggedData;
  } | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setFailed(false);
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Rows AND dealers are load-bearing (joinless rows get their lat/lng from
    // the dealer join) — retry then surface an error state. Flags degrade to
    // empty: the map is still useful without report flags.
    Promise.all([
      withRetry(() =>
        fetchJson<InventoryDealerJoin[]>(
          `/api/inventory/mapRows/${model}`,
          `/api/inventory/mapData/${model}`,
          `/api/inventory/data/${model}`,
        ),
      ),
      withRetry(() => fetchJson<Dealer[]>(`/api/inventory/dealerData/${make}`)),
      withRetry(() =>
        fetchJson<FlaggedData>('/api/inventory/allReportIDs'),
      ).catch(() => EMPTY_FLAGGED),
    ])
      .then(([rows, dealers, flagged]) => {
        if (!cancelled)
          setData({ markers: joinDealers(rows, dealers), dealers, flagged });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [model, make, attempt]);

  if (failed) {
    return (
      <div style={errorWrap} role="alert">
        <span>couldn&apos;t load inventory — check your connection</span>
        <button
          type="button"
          onClick={retry}
          style={{
            font: 'inherit',
            color: 'inherit',
            border: '1px solid currentColor',
            borderRadius: 6,
            padding: '6px 16px',
            background: 'transparent',
            cursor: 'pointer',
          }}
        >
          retry
        </button>
      </div>
    );
  }
  if (!data) return <StockMapLoading />;
  return (
    <MapComponent
      allMarkers={data.markers}
      allDealers={data.dealers}
      flagged={data.flagged}
      initialSubmodel={initialSubmodel}
    />
  );
}
